/* ==========================================================================
   SMT Story System  —  smt-sys.js  (v1.0.0)
   Storefront behaviour, vanilla, ~2 KB:
     - the hero's boot readout ends (the start is set inline before first paint,
       once per visit; skipped for reduced motion)
     - sections below the fold rise in once as they arrive
     - the world carousel on phones: position bars that also jump to a world
     - the chapter file's stage index follows the reader (aria-current)
   Loaded with `defer` by the storefront sections; guarded so it runs once.
   Nothing here is needed to shop: every link and form works without it.
   ========================================================================== */
(function () {
  'use strict';
  if (window.SMTSys) return;
  var API = (window.SMTSys = { version: '1.0.0' });
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var design = !!(window.Shopify && window.Shopify.designMode);
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---- hero: the readout was started inline; end it cleanly ---- */
  function hero(el) {
    if (el.dataset.sysReady) return;
    el.dataset.sysReady = '1';
    if (!el.classList.contains('is-booting')) return;
    setTimeout(function () { el.classList.remove('is-booting'); el.classList.add('is-booted'); }, 2600);
  }

  /* ---- reveal: only what starts below the fold is hidden, so nothing flickers ---- */
  var io = null;
  function reveal(root) {
    if (design || reduced.matches || !('IntersectionObserver' in window)) return;
    var els = $$('[data-sys-reveal]:not(.is-in)', root);
    if (!els.length) return;
    var fold = window.innerHeight * 0.92;
    els.forEach(function (el) { if (el.getBoundingClientRect().top < fold) el.classList.add('is-in'); });
    document.documentElement.classList.add('sys-rv-armed');
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    els.forEach(function (el) { if (!el.classList.contains('is-in')) io.observe(el); });
  }

  /* ---- worlds: position bars for the phone carousel ---- */
  function worlds(sec) {
    if (sec.dataset.sysReady) return;
    sec.dataset.sysReady = '1';
    var track = sec.querySelector('[data-sys-track]');
    var dots = sec.querySelector('[data-sys-dots]');
    if (!track || !dots) return;
    var items = Array.prototype.slice.call(track.children);
    if (items.length < 2) { dots.hidden = true; return; }
    dots.innerHTML = '';
    items.forEach(function (it, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Show world ' + (i + 1) + ' of ' + items.length);
      b.addEventListener('click', function () {
        it.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
      });
      dots.appendChild(b);
    });
    var bars = $$('button', dots), ticking = false;
    function sync() {
      ticking = false;
      var tr = track.getBoundingClientRect(), mid = tr.left + tr.width / 2, best = 0, bd = Infinity;
      items.forEach(function (it, i) {
        var r = it.getBoundingClientRect(), d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bd) { bd = d; best = i; }
      });
      bars.forEach(function (b, i) { b.setAttribute('aria-current', i === best ? 'true' : 'false'); });
    }
    track.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(sync); } }, { passive: true });
    sync();
  }

  /* ---- chapter file: the stage index follows the reader ---- */
  function file(sec) {
    if (sec.dataset.sysReady) return;
    sec.dataset.sysReady = '1';
    var links = $$('.sys-file__index a[href^="#"]', sec);
    if (links.length < 2 || !('IntersectionObserver' in window)) return;
    var strip = sec.querySelector('.sys-file__index ol');
    var byId = {}, stages = [];
    links.forEach(function (a) {
      var el = sec.querySelector(a.getAttribute('href'));
      if (el) { byId[el.id] = a; stages.push(el); }
    });
    if (stages.length < 2) return;
    var current = null;
    function mark(el) {
      if (!el || el === current) return;
      current = el;
      links.forEach(function (a) { a.removeAttribute('aria-current'); });
      var a = byId[el.id];
      a.setAttribute('aria-current', 'true');
      if (strip && strip.scrollWidth > strip.clientWidth) {
        var left = a.offsetLeft - (strip.clientWidth - a.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: reduced.matches ? 'auto' : 'smooth' });
      }
    }
    var seen = {};
    var fio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { seen[e.target.id] = e.isIntersecting; });
      var top = null;
      for (var i = 0; i < stages.length; i++) { if (seen[stages[i].id]) { top = stages[i]; break; } }
      if (!top) {
        var y = window.innerHeight * 0.35;
        for (var j = stages.length - 1; j >= 0; j--) { if (stages[j].getBoundingClientRect().top <= y) { top = stages[j]; break; } }
      }
      mark(top || stages[0]);
    }, { rootMargin: '-30% 0px -55% 0px', threshold: 0 });
    stages.forEach(function (el) { fio.observe(el); });
  }

  function boot(root) {
    $$('[data-sys-hero]', root).forEach(hero);
    $$('[data-sys-worlds]', root).forEach(worlds);
    $$('[data-sys-file]', root).forEach(file);
    reveal(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  API.boot = boot;
})();
